import { useFlag } from '@reflag/react-sdk';
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { memo } from 'react';
import { atom, atomFamily, useRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type {
    NavigationScreenContentGapsState,
    NavigationScreenSiteState,
    NavigationScreenState,
    NavigationScreenTriageState,
} from '@gitbook/app-navigation';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    DialogRoot,
    DialogTrigger,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    ScrollFader,
    Sidebar,
    SidebarActionButton,
    SidebarBody,
    SidebarButton,
    SidebarDisclosureMenu,
    SidebarFooter,
    SidebarHeader,
    SidebarHeading,
    SidebarItemList,
    SidebarItemSkeleton,
    SidebarLink,
    type SidebarLinkProps,
    SidebarToggleButton,
    Status,
    TextInput,
    Tooltip,
    useScrollFaderController,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import {
    APIErrorBoundary,
    CreateSiteDialog,
    getFallbackGitSyncCurrentStatus,
    getGitSyncStatusPresentation,
    getSiteCustomizationTabSiteSpaceId,
    getSiteGitSyncSetupTarget,
    hasGitSyncInstallation,
    isFailedGitSyncStatus,
    NewFeatureBadge,
    NotificationsBell,
    OrganizationSearchButton,
    SiteContentIcon,
    SiteSortBy,
    siteCustomizationTabs,
    useSortSites,
} from '../../Components';
import { CreateRootSiteItemButton } from '../../Components/Site/SiteStructure/CreateRootSiteItemButton';
import { confirmDetachStyleguide } from '../../Components/Styleguide';
import { useNavigation } from '../../navigation';
import { useVisibleSiteSettingsSections } from '../../Screens/Site/components/Settings';
import {
    apiQuery,
    desktopSidebarCollapsedAtom,
    type SiteCapability,
    type UseUpgradeSite,
    useIsSidebarLocked,
    useTrialingOrganizationFeaturePlan,
    useTrialingSiteFeaturePlan,
    useUnlockSidebar,
    useUpgradeSite,
} from '../../state';
import { createDocumentationURL, persistRecoilEffect } from '../../utils';
import { SidebarActionsButton } from '../SidebarActionsButton';
import { SidebarTrialingBadge } from '../SidebarTrialingBadge';
import { isOrgLevelScreen, shouldShowOrgContentSidebar } from './ContentSidebar.utils';
import { ContentSidebarContentWithOrgLegacy } from './ContentSidebarLegacy';
import { CustomerIOAlert } from './CustomerIOAlert';
import { FeedbackSidebarButton } from './FeedbackSidebarButton';
import { HelpButton } from './HelpButton';
import {
    CollapsedOrganizationTrialAlertButton,
    CollapsibleOrganizationAlert,
    OrganizationAlert,
} from './OrganizationAlert';
import { OrganizationButton } from './OrganizationButton';
import { SiteCentricNavConfirmDialog } from './SiteCentricNavConfirmDialog';
import { preloadSiteCentricNavConfirmDialogImage } from './SiteCentricNavConfirmDialogAssets';
import { SiteCentricWelcomeDialog } from './SiteCentricWelcomeDialog';
import { SiteSwitcherButton } from './SiteSwitcherButton';
import { SidebarSiteTree, useCollapseAllSiteItems } from './Sites';
import { SpacesTree } from './Spaces';
export interface ContentSidebarProps {
    screen: NavigationScreenState;
}

/**
 * Content sidebar.
 *
 * Renders  the site-centric or legacy content sidebar based on the `SITE_CENTRIC` feature flag.
 */
export function ContentSidebar(props: ContentSidebarProps) {
    return (
        <Sidebar>
            <ContentSidebarContent {...props} />
        </Sidebar>
    );
}

function ContentSidebarContent(props: ContentSidebarProps) {
    const { screen } = props;

    const siteCentricNav = useFlag('SITE_CENTRIC');

    if (!('organization' in screen) || !screen.organization) {
        return <NoOrganizationEmptyState />;
    }

    if (siteCentricNav.isLoading) {
        return <ContentSidebarSkeleton />;
    }

    return (
        <APIErrorBoundary
            fallback={{
                404: <NoOrganizationEmptyState />,
                403: <NoOrganizationEmptyState />,
            }}
        >
            {siteCentricNav.isEnabled ? (
                <ContentSidebarContentWithOrg
                    screen={screen}
                    organizationId={screen.organization}
                />
            ) : (
                <ContentSidebarContentWithOrgLegacy
                    screen={screen}
                    organizationId={screen.organization}
                />
            )}
        </APIErrorBoundary>
    );
}

function NoOrganizationEmptyState() {
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Organization />
            </EmptyStateIcon>
            <Description>
                Join an organization or create a new one to get started with your content.
            </Description>
            <EmptyStateActions>
                <Button
                    kind="secondary"
                    label="Create organization"
                    href={{ screen: { type: 'welcome' }, overlay: null }}
                />
            </EmptyStateActions>
        </EmptyState>
    );
}

/**
 * Memoized content of the sidebar, optimized to re-render only when relevant url parts change.
 */
function ContentSidebarContentWithOrg(props: {
    organizationId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, screen } = props;
    const siteId = 'site' in screen ? screen.site : undefined;
    const scrollFader = useScrollFaderController('vertical');

    const [{ data: organization }, { data: orgAllContent }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId }),
            apiQuery('getOrganizationAllContent', { organizationId }),
        ],
    });
    const siteContent = siteId
        ? orgAllContent.sites.find((item) => item.site.id === siteId)
        : undefined;
    const site = siteContent?.site ?? null;
    const structure = siteContent?.structure ?? null;

    return (
        <>
            <ContentSidebarHeader
                organization={organization}
                organizationContent={orgAllContent}
                screen={screen}
                site={site}
            />

            <ScrollFader
                controller={scrollFader}
                kind="sidebar"
                className="!h-auto flex min-h-0 flex-1 flex-col before:to-[8%] after:to-[8%]"
            >
                <SidebarBody
                    ref={scrollFader.ref}
                    fullWidth
                    aria-label="Main navigation"
                    className="min-h-0 scroll-pb-12 pb-10 [scrollbar-gutter:stable]"
                >
                    {shouldShowOrgContentSidebar(screen, orgAllContent) ? (
                        <OrgContentSidebar
                            content={orgAllContent}
                            organization={organization}
                            screen={screen}
                        />
                    ) : (
                        <SiteContentSidebar
                            site={site}
                            structure={structure}
                            organization={organization}
                            orgAllContent={orgAllContent}
                            screen={screen}
                        />
                    )}
                </SidebarBody>
            </ScrollFader>

            <ContentSidebarFooter organization={organization} screen={screen} />
            <SiteCentricWelcomeDialog />
        </>
    );
}

function OrgContentSidebar(props: {
    content: api.OrganizationAllContent;
    organization: api.Organization;
    screen: NavigationScreenState;
}) {
    const {
        content,
        screen,
        organization: { id: organizationId, permissions },
    } = props;
    const [isContentSectionExpanded, setIsContentSectionExpanded] = useRecoilState(
        toggleableSidebarSectionExpandedAtoms.content
    );
    const mcpFlag = useFlag('MCP_SERVER');
    const isMcpEnabled = !mcpFlag.isLoading && mcpFlag.isEnabled;
    const isStyleguideEnabled = useFlag('STYLEGUIDE').isEnabled;
    const shouldShowSpaces = screen.type === 'space' && !screen.site;
    // A styleguide opened at the org level (no site context) shows the list of the
    // organization's styleguides instead of the regular spaces tree.
    const isStyleguideSpace =
        isStyleguideEnabled &&
        screen.type === 'space' &&
        !screen.site &&
        content.sites.some((item) => item.site.styleguide?.space === screen.space);

    return (
        <div className="flex flex-col gap-4">
            <SidebarItemList fullWidth>
                <HomeLink screen={screen} />
                <SitesLink label="Docs sites" screen={screen} />
                <ContentScreenLink screen={screen} />
                {permissions.createContent && content.deletedSpaces.length > 0 ? (
                    <OrganizationTrashLink screen={screen} organizationId={organizationId} />
                ) : null}
                {permissions.listMembers ? (
                    <OpenAPISettingsLink organizationId={organizationId} />
                ) : null}
                {permissions.listMembers ? (
                    <TranslationsSettingsLink organizationId={organizationId} />
                ) : null}
                {permissions.listIntegrations ? (
                    <IntegrationsSettingsLink organizationId={organizationId} />
                ) : null}
                {isMcpEnabled ? <McpSettingsLink /> : null}
            </SidebarItemList>

            {permissions.admin ? (
                <SidebarItemList fullWidth>
                    <SidebarHeading label="Organization" />
                    <OrganizationSettingsLink organizationId={organizationId} />
                    <MembersLink screen={screen} organizationId={organizationId} />
                    <BillingLink screen={screen} organizationId={organizationId} />
                </SidebarItemList>
            ) : null}

            {!shouldShowSpaces ? (
                <DocsSitesSection content={content} organization={props.organization} />
            ) : null}

            {shouldShowSpaces ? (
                <SidebarDisclosureMenu
                    kind="section"
                    label={isStyleguideSpace ? 'Styleguides' : 'Content'}
                    isExpanded={isContentSectionExpanded}
                    onExpandedChange={setIsContentSectionExpanded}
                    toggleVisibility="active"
                    className="pr-1.5"
                >
                    {isStyleguideSpace ? (
                        <React.Suspense fallback={<SidebarItemSkeleton />}>
                            <StyleguidesContentList
                                organizationId={organizationId}
                                screen={screen}
                            />
                        </React.Suspense>
                    ) : (
                        <SpacesTree
                            organization={props.organization}
                            content={content}
                            screen={screen}
                        />
                    )}
                </SidebarDisclosureMenu>
            ) : null}
        </div>
    );
}

function DocsSitesSection(props: {
    content: api.OrganizationAllContent;
    organization: api.Organization;
}) {
    const { content, organization } = props;
    const { sortedSites } = useSortSites({
        allSites: content.sites,
        sortBy: SiteSortBy.Viewed,
        organizationId: organization.id,
        limit: 10,
    });
    const visibleSites = sortedSites.map(({ item }) => item);

    return (
        <SidebarItemList fullWidth>
            <div className="flex items-center justify-between">
                <SidebarHeading label="Docs sites" />

                {organization.permissions.createContent ? (
                    <DialogRoot>
                        <DialogTrigger>
                            <SidebarActionButton
                                label="Create a site"
                                icon={Icon.Plus}
                                className="group-hover/sidebar:opacity-100 group-has-[:focus-visible]/sidebar:opacity-100"
                            />
                        </DialogTrigger>
                        <CreateSiteDialog organizationId={organization.id} />
                    </DialogRoot>
                ) : null}
            </div>

            {visibleSites.map(({ site }) => (
                <SidebarLink
                    key={site.id}
                    label={site.title}
                    icon={<SiteContentIcon site={site} />}
                    href={{
                        screen: {
                            type: 'site',
                            organization: organization.id,
                            site: site.id,
                        },
                    }}
                />
            ))}

            {content.sites.length > visibleSites.length ? (
                <SidebarLink
                    label="See all"
                    icon={Icon.MoreHorizontal}
                    href={{
                        screen: {
                            type: 'sites',
                            organization: organization.id,
                        },
                    }}
                />
            ) : null}
        </SidebarItemList>
    );
}

function SiteContentSidebar(props: {
    site: api.Site | null;
    structure: api.SiteStructure | null;
    organization: api.Organization;
    orgAllContent: api.OrganizationAllContent;
    screen: NavigationScreenState;
}) {
    const { site, structure, ...rest } = props;

    if (!site) {
        // This state can happen when a screen triggered a redirection
        // so we show a loading state in the sidebar while we fetch the data.
        return <ContentSidebarSkeleton />;
    }

    if (!structure || structure.structure.length === 0) {
        return null;
    }

    return <SiteContentSidebarWithStructure site={site} structure={structure} {...rest} />;
}

function SiteContentSidebarWithStructure(props: {
    site: api.Site;
    structure: api.SiteStructure;
    organization: api.Organization;
    orgAllContent: api.OrganizationAllContent;
    screen: NavigationScreenState;
}) {
    const { organization, orgAllContent, screen, site, structure } = props;
    const { isEnabled: isContextChangeRequestEnabled } = useFlag('SITE_CONTEXT_CHANGE_REQUEST');
    const isNativeGitSyncSitesFlagEnabled = useFlag('NATIVE_GIT_SYNC_SITES').isEnabled;

    const sitePermissions = React.useMemo<SiteCapability>(
        () => ({
            canAdminSite: site.permissions.admin,
            canViewSite: site.permissions.access,
            installIntegration: site.permissions.installIntegration,
        }),
        [site]
    );
    const visibleSettingsSections = useVisibleSiteSettingsSections(site, organization);
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesSections);
    const {
        trialingSectionsPlan,
        trialingRedirectsPlan,
        trialingAiInsightsPlan,
        trialingConnectionsPlan,
        trialingChannelsPlan,
    } = useSiteNavigationTrialingPlans(site);
    const [isGeneralSectionExpanded, setIsGeneralSectionExpanded] = useRecoilState(
        toggleableSidebarSectionExpandedAtoms.general
    );
    const [isToolsSectionExpanded, setIsToolsSectionExpanded] = useRecoilState(
        toggleableSidebarSectionExpandedAtoms.tools
    );

    const currentSiteView = screen.type === 'site' ? screen.tab : undefined;
    const getSiteScreenHref = (
        tab: SiteView,
        sideSheet?: NavigationScreenSiteState['sideSheet']
    ): SidebarLinkProps['href'] => ({
        screen: {
            type: 'site',
            organization: organization.id,
            site: site.id,
            tab,
            sideSheet,
        },
    });

    const {
        canViewSiteTools,
        expandedMenu,
        showCustomizeMenu,
        showExtendMenu,
        showSettingsMenu,
        toggleExpandedMenu,
    } = useSiteToolsExpandableMenus({
        currentSiteView,
        screen,
        sitePermissions,
        visibleSettingsSections,
    });

    return (
        <nav aria-label="Site navigation" className="flex w-full flex-none flex-col gap-4">
            <SidebarDisclosureMenu
                kind="section"
                label="General"
                isExpanded={isGeneralSectionExpanded}
                onExpandedChange={setIsGeneralSectionExpanded}
                toggleVisibility="active"
                className="pr-1.5"
            >
                <OverviewLink screen={screen} />
                <ChangeRequestsLink screen={screen} />
                {isContextChangeRequestEnabled && (
                    <>
                        <SiteTriageLink
                            organizationId={organization.id}
                            siteId={site.id}
                            screen={screen}
                        />
                        <SiteContentGapsLink
                            organizationId={organization.id}
                            siteId={site.id}
                            screen={screen}
                        />
                    </>
                )}
                {sitePermissions.canAdminSite ? (
                    <StructureLink screen={screen} trialingPlan={trialingSectionsPlan} />
                ) : null}
                {showSettingsMenu ? (
                    <SidebarDisclosureMenu
                        label="Settings"
                        icon={Icon.Settings}
                        isExpanded={expandedMenu === 'settings'}
                        onExpandedChange={() => {
                            toggleExpandedMenu('settings');
                        }}
                    >
                        {visibleSettingsSections.general ? (
                            <SidebarLink
                                label="General"
                                icon={Icon.Options02}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'general'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'general',
                                })}
                            />
                        ) : null}
                        {sitePermissions.canAdminSite ? <SiteMembersLink screen={screen} /> : null}
                        {visibleSettingsSections.agent ? (
                            <SidebarLink
                                label="Agents"
                                icon={Icon.AIAgent}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'agent'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'agent',
                                })}
                            />
                        ) : null}
                        {visibleSettingsSections.audience ? (
                            <SidebarLink
                                label="Audience"
                                icon={Icon.PublishStatusPublic}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    (currentSiteView.section === 'audience' ||
                                        currentSiteView.section === 'schema')
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'audience',
                                })}
                            />
                        ) : null}
                        {visibleSettingsSections.domains ? (
                            <SidebarLink
                                label="Domain and URL"
                                icon={Icon.Globe}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'domains'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'domains',
                                })}
                            />
                        ) : null}
                        {visibleSettingsSections.redirects ? (
                            <SidebarLink
                                label="Redirects"
                                icon={Icon.Redirect}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'redirects'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'redirects',
                                })}
                            >
                                <SidebarTrialingBadge plan={trialingRedirectsPlan} />
                            </SidebarLink>
                        ) : null}
                        {visibleSettingsSections.sponsorship ? (
                            <SidebarLink
                                label="Ads and sponsorship"
                                icon={Icon.PaidMember}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'sponsorship'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'sponsorship',
                                })}
                            />
                        ) : null}
                        {visibleSettingsSections.plan ? (
                            <SidebarLink
                                label="Plan"
                                icon={Icon.Upgrade}
                                isActive={
                                    currentSiteView?.type === 'settings' &&
                                    currentSiteView.section === 'plan'
                                }
                                href={getSiteScreenHref({
                                    type: 'settings',
                                    section: 'plan',
                                })}
                            />
                        ) : null}
                    </SidebarDisclosureMenu>
                ) : null}
                {/* Up for discussion: <NotificationsBell label="Inbox" icon={Icon.Files} showCount /> */}
            </SidebarDisclosureMenu>

            {/* Tools */}
            {canViewSiteTools ? (
                <SidebarDisclosureMenu
                    kind="section"
                    label="Tools"
                    isExpanded={isToolsSectionExpanded}
                    onExpandedChange={setIsToolsSectionExpanded}
                    toggleVisibility="active"
                    className="pr-1.5"
                >
                    {sitePermissions.canAdminSite && isNativeGitSyncSitesFlagEnabled ? (
                        <GitSyncSidebarLink
                            site={site}
                            organizationId={organization.id}
                            screen={screen}
                        />
                    ) : null}

                    <SiteStyleguideLink
                        site={site}
                        organizationId={organization.id}
                        screen={screen}
                        canAdminSite={sitePermissions.canAdminSite}
                    />

                    {showCustomizeMenu ? (
                        <SidebarDisclosureMenu
                            label="Customize"
                            icon={Icon.Customization}
                            isExpanded={expandedMenu === 'customize'}
                            onExpandedChange={() => {
                                toggleExpandedMenu('customize');
                            }}
                        >
                            {sitePermissions.canAdminSite
                                ? siteCustomizationTabs.map((tab) => (
                                      <SidebarLink
                                          key={tab.id}
                                          label={tab.label}
                                          icon={tab.icon}
                                          isActive={
                                              currentSiteView?.type === 'customization' &&
                                              currentSiteView.tab === tab.id
                                          }
                                          href={getSiteScreenHref({
                                              type: 'customization',
                                              tab: tab.id,
                                              siteSpaceId: getSiteCustomizationTabSiteSpaceId(
                                                  tab.id,
                                                  currentSiteView?.type === 'customization'
                                                      ? currentSiteView.siteSpaceId
                                                      : undefined
                                              ),
                                          })}
                                      />
                                  ))
                                : null}
                        </SidebarDisclosureMenu>
                    ) : null}

                    <SidebarDisclosureMenu
                        label="Analyze"
                        icon={Icon.Insights}
                        isExpanded={expandedMenu === 'analyze'}
                        onExpandedChange={() => {
                            toggleExpandedMenu('analyze');
                        }}
                    >
                        <SidebarLink
                            label="AI Insights"
                            icon={Icon.Insights}
                            isActive={currentSiteView?.type === 'insights'}
                            href={getSiteScreenHref({
                                type: 'insights',
                                view: { type: 'home' },
                            })}
                        >
                            <SidebarTrialingBadge plan={trialingAiInsightsPlan} />
                        </SidebarLink>
                        <SidebarLink
                            label="Analytics"
                            icon={Icon.MaximumValue}
                            isActive={currentSiteView?.type === 'analytics'}
                            href={getSiteScreenHref({
                                type: 'analytics',
                                report: 'traffic',
                            })}
                        />
                    </SidebarDisclosureMenu>

                    {showExtendMenu ? (
                        <SidebarDisclosureMenu
                            label="Extend"
                            icon={Icon.Extend}
                            isExpanded={expandedMenu === 'extend'}
                            onExpandedChange={() => {
                                toggleExpandedMenu('extend');
                            }}
                        >
                            {visibleSettingsSections.connections ? (
                                <SidebarLink
                                    label="Connections"
                                    icon={Icon.Connectors}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'connections'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'connections',
                                    })}
                                >
                                    <SidebarTrialingBadge plan={trialingConnectionsPlan} />
                                </SidebarLink>
                            ) : null}
                            {visibleSettingsSections.channels ? (
                                <SidebarLink
                                    label="Channels"
                                    icon={Icon.Channel}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'channels'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'channels',
                                    })}
                                >
                                    <SidebarTrialingBadge plan={trialingChannelsPlan} />
                                </SidebarLink>
                            ) : null}
                            {visibleSettingsSections.embed ? (
                                <SidebarLink
                                    label="Docs Embed"
                                    icon={Icon.DocsEmbed}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'embed'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'embed',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.mcp ? (
                                <SidebarLink
                                    label="MCP access"
                                    icon={Icon.MCP}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'mcp'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'mcp',
                                    })}
                                />
                            ) : null}
                            <SidebarLink
                                label="Integrations"
                                icon={Icon.Integrations}
                                isActive={
                                    screen.type === 'site' &&
                                    screen.sideSheet?.type === 'integrations'
                                }
                                href={getSiteScreenHref(currentSiteView, {
                                    type: 'integrations',
                                })}
                            />
                        </SidebarDisclosureMenu>
                    ) : null}
                </SidebarDisclosureMenu>
            ) : null}

            <SiteContentSidebarSiteStructure
                organization={organization}
                orgAllContent={orgAllContent}
                screen={screen}
                site={site}
                sitePermissions={sitePermissions}
                structure={structure}
                upgrade={upgrade}
            />
        </nav>
    );
}

function useSiteNavigationTrialingPlans(site: api.Site) {
    const trialingSectionsPlan = useTrialingSiteFeaturePlan(site, api.SiteFeatureKey.SitesSections);
    const trialingRedirectsPlan = useTrialingSiteFeaturePlan(
        site,
        api.SiteFeatureKey.SitesRedirects
    );
    const trialingAiInsightsPlan = useTrialingSiteFeaturePlan(
        site,
        api.SiteFeatureKey.SitesAiInsights
    );
    const trialingConnectionsPlan = useTrialingSiteFeaturePlan(
        site,
        api.SiteFeatureKey.SitesConnections
    );
    const trialingChannelsPlan = useTrialingSiteFeaturePlan(site, api.SiteFeatureKey.SitesChannels);

    return {
        trialingSectionsPlan,
        trialingRedirectsPlan,
        trialingAiInsightsPlan,
        trialingConnectionsPlan,
        trialingChannelsPlan,
    };
}

const toggleableSidebarSectionExpandedAtoms = {
    content: atom<boolean>({
        key: 'orgSidebarContentSectionExpanded',
        default: true,
        effects: [persistRecoilEffect()],
    }),
    general: atom<boolean>({
        key: 'siteSidebarGeneralSectionExpanded',
        default: true,
        effects: [persistRecoilEffect()],
    }),
    tools: atom<boolean>({
        key: 'siteSidebarToolsSectionExpanded',
        default: true,
        effects: [persistRecoilEffect()],
    }),
};

const organizationTrialAlertCollapsedAtom = atomFamily<boolean, string>({
    key: 'organizationTrialAlertCollapsed',
    default: false,
    effects: [persistRecoilEffect({ storage: 'localStorage' })],
});

type SiteDisclosureMenuId = 'customize' | 'analyze' | 'extend' | 'settings';
type SiteView = NavigationScreenSiteState['tab'];

function useSiteToolsExpandableMenus(props: {
    currentSiteView: SiteView | undefined;
    screen: NavigationScreenState;
    sitePermissions: SiteCapability;
    visibleSettingsSections: ReturnType<typeof useVisibleSiteSettingsSections>;
}) {
    const { currentSiteView, screen, sitePermissions, visibleSettingsSections } = props;

    const canViewSiteTools = sitePermissions.canViewSite || sitePermissions.canAdminSite;
    const showCustomizeMenu = sitePermissions.canAdminSite;
    const showExtendMenu = sitePermissions.canAdminSite;
    const showSettingsMenu =
        sitePermissions.canAdminSite &&
        (visibleSettingsSections.general ||
            visibleSettingsSections.agent ||
            visibleSettingsSections.audience ||
            visibleSettingsSections.domains ||
            visibleSettingsSections.redirects ||
            visibleSettingsSections.sponsorship ||
            visibleSettingsSections.plan);

    const isCustomizeActive = currentSiteView?.type === 'customization';
    const isAnalyzeActive =
        currentSiteView?.type === 'analytics' || currentSiteView?.type === 'insights';
    const isExtendActive =
        currentSiteView?.type === 'settings' &&
        ((visibleSettingsSections.mcp && currentSiteView.section === 'mcp') ||
            (visibleSettingsSections.connections && currentSiteView.section === 'connections') ||
            (visibleSettingsSections.channels && currentSiteView.section === 'channels') ||
            (visibleSettingsSections.embed && currentSiteView.section === 'embed'));
    const isIntegrationsActive =
        screen.type === 'site' && screen.sideSheet?.type === 'integrations';
    const isSettingsActive =
        currentSiteView?.type === 'members' ||
        (currentSiteView?.type === 'settings' &&
            ((visibleSettingsSections.general && currentSiteView.section === 'general') ||
                (visibleSettingsSections.agent && currentSiteView.section === 'agent') ||
                (visibleSettingsSections.audience &&
                    (currentSiteView.section === 'audience' ||
                        currentSiteView.section === 'schema')) ||
                (visibleSettingsSections.domains && currentSiteView.section === 'domains') ||
                (visibleSettingsSections.redirects && currentSiteView.section === 'redirects') ||
                (visibleSettingsSections.sponsorship &&
                    currentSiteView.section === 'sponsorship') ||
                (visibleSettingsSections.plan && currentSiteView.section === 'plan')));

    const activeMenu: SiteDisclosureMenuId | null = canViewSiteTools
        ? showCustomizeMenu && isCustomizeActive
            ? 'customize'
            : isAnalyzeActive
              ? 'analyze'
              : showExtendMenu && (isExtendActive || isIntegrationsActive)
                ? 'extend'
                : showSettingsMenu && isSettingsActive
                  ? 'settings'
                  : null
        : null;

    const [expandedMenu, setExpandedMenu] = React.useState<SiteDisclosureMenuId | null>(activeMenu);
    const toggleExpandedMenu = useEventCallback((menu: SiteDisclosureMenuId) => {
        setExpandedMenu((current) => (current === menu ? null : menu));
    });

    React.useEffect(() => {
        if (activeMenu) {
            setExpandedMenu(activeMenu);
        }
    }, [activeMenu]);

    return {
        canViewSiteTools,
        expandedMenu,
        showCustomizeMenu,
        showExtendMenu,
        showSettingsMenu,
        toggleExpandedMenu,
    };
}

function SiteContentSidebarSiteStructure(props: {
    site: api.Site;
    structure: api.SiteStructure;
    organization: api.Organization;
    orgAllContent: api.OrganizationAllContent;
    screen: NavigationScreenState;
    sitePermissions: SiteCapability;
    upgrade: UseUpgradeSite | null;
}) {
    const { organization, orgAllContent, screen, site, sitePermissions, structure, upgrade } =
        props;

    const { hasExpandedItems, collapseAllItems } = useCollapseAllSiteItems();

    const [showSearch, setShowSearch] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const isSearchActive = showSearch && query.trim().length > 0;

    return (
        <div className="flex min-h-52 flex-none flex-col gap-px">
            <div className="flex items-center justify-between px-3 pr-1.5">
                <SidebarHeading label="Content" />

                <div className="pointer-events-none flex items-center gap-1 opacity-0 transition-opacity group-hover/sidebar:pointer-events-auto group-hover/sidebar:opacity-100 has-[[aria-expanded=true]]:pointer-events-auto has-[[aria-expanded=true]]:opacity-100 group-has-[:focus-visible]/sidebar:pointer-events-auto group-has-[[aria-haspopup=true][aria-expanded=true]]/sidebar:pointer-events-auto group-has-[:focus-visible]/sidebar:opacity-100 group-has-[[aria-haspopup=true][aria-expanded=true]]/sidebar:opacity-100">
                    {!isSearchActive ? (
                        <Button
                            label="Collapse all"
                            leadingIcon={Icon.Collapse}
                            iconOnly
                            kind="blank"
                            size="small"
                            disabled={!hasExpandedItems}
                            aria-hidden={!hasExpandedItems}
                            onPress={collapseAllItems}
                            className={clsx(
                                hasExpandedItems
                                    ? 'opacity-1'
                                    : 'pointer-events-none opacity-0 transition-opacity'
                            )}
                        />
                    ) : null}

                    <Button
                        label={showSearch ? 'Hide site search' : 'Show site search'}
                        leadingIcon={Icon.Search}
                        iconOnly
                        kind="blank"
                        size="small"
                        onPress={() => {
                            if (showSearch) {
                                setQuery('');
                            }
                            setShowSearch((prev) => !prev);
                        }}
                    />

                    {sitePermissions.canAdminSite ? (
                        <>
                            <Button
                                label="Manage site structure"
                                leadingIcon={Icon.Edit}
                                iconOnly
                                kind="blank"
                                size="small"
                                href={{
                                    screen: {
                                        type: 'site',
                                        organization: organization.id,
                                        site: site.id,
                                        tab: { type: 'structure' },
                                    },
                                }}
                            />

                            <CreateRootSiteItemButton
                                label="Add content"
                                organization={organization}
                                site={site}
                                siteStructure={structure}
                                orgAllContent={orgAllContent}
                                upgrade={upgrade}
                                iconOnly
                                size="small"
                                kind="blank"
                            />
                        </>
                    ) : null}
                </div>
            </div>

            <div className="w-full overflow-x-hidden overflow-y-visible pr-1.5 pl-3">
                <div
                    aria-hidden={!showSearch}
                    className="grid transition-[grid-template-rows] duration-200 ease-out"
                    style={{ gridTemplateRows: showSearch ? '1fr' : '0fr' }}
                >
                    <div className="overflow-hidden px-1 pt-1">
                        <TextInput
                            autoFocus={showSearch}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            onClear={() => setQuery('')}
                            leadingIcon={Icon.Search}
                            placeholder="Search site content"
                            className="mb-2 w-full"
                            disabled={!showSearch}
                        />
                    </div>
                </div>

                <SidebarSiteTree
                    siteContent={{ site, structure }}
                    organization={organization}
                    organizationId={organization.id}
                    orgAllContent={orgAllContent}
                    screen={screen}
                    sitePermissions={sitePermissions}
                    upgrade={upgrade}
                    searchQuery={showSearch ? query : ''}
                />
            </div>
        </div>
    );
}

const ContentSidebarHeader = memo(function ContentSidebarHeader(props: {
    site: api.Site | null;
    screen: NavigationScreenState;
    organization: api.Organization;
    organizationContent: api.OrganizationAllContent;
}) {
    const { organization, organizationContent, screen, site } = props;

    const [isCollapsed, setCollapse] = useRecoilState(desktopSidebarCollapsedAtom);
    const isLocked = useIsSidebarLocked();
    const unlock = useUnlockSidebar();

    const siteId = 'site' in screen && screen.site;

    return (
        <SidebarHeader>
            <div className="flex min-w-0 flex-1 flex-col space-y-3">
                <div className="flex w-full min-w-0 items-center">
                    <div className="flex flex-1">
                        <OrganizationButton
                            organization={organization}
                            organizationContent={organizationContent}
                            screen={screen}
                            iconOnly={!isOrgLevelScreen(screen)}
                        />
                    </div>

                    <div className="flex items-center gap-0.5">
                        {siteId ? (
                            <SidebarLink
                                iconOnly
                                icon={Icon.Home}
                                label="Home"
                                onPress={() => {
                                    analytics.logEvent({
                                        type: 'click',
                                        action: 'open',
                                        organization: organization.id,
                                        screen: screen.type,
                                        screenSection: 'sidebar_header',
                                        component: 'home_link',
                                    });
                                }}
                                href={{
                                    screen: {
                                        type: 'home',
                                        organization: organization.id,
                                    },
                                }}
                            />
                        ) : null}
                        <OrganizationSearchButton
                            data-testid="sidebar.findButton"
                            organization={organization}
                        />
                        <NotificationsBell iconOnly placement="bottom-start" />

                        {/* We only show the toggle button when the sidebar is closed.
                Collapsing it handled only by the sidebar resize bar. */}
                        {isCollapsed && (
                            <SidebarToggleButton
                                isCollapsed={isLocked || isCollapsed}
                                onCollapse={() => {
                                    setCollapse(true);
                                }}
                                onUncollapse={() => {
                                    unlock();
                                    setCollapse(false);
                                }}
                                outlined={false}
                            />
                        )}
                    </div>
                </div>

                {siteId && site ? (
                    <SiteSwitcherButton
                        screen={screen}
                        organization={organization}
                        canCreateSite={organization.permissions.createContent}
                        organizationContent={organizationContent}
                        site={site}
                    />
                ) : null}
            </div>
        </SidebarHeader>
    );
});

export const ContentSidebarFooter = memo(function ContentSidebarFooter(props: {
    organization: api.Organization;
    screen: NavigationScreenState;
}) {
    const { organization, screen } = props;
    const { navigate } = useNavigation();

    const siteCentricNav = useFlag('SITE_CENTRIC');
    const isSiteCentricNavEnabled = !siteCentricNav.isLoading && siteCentricNav.isEnabled;
    const canToggleSiteCentricNav = useFlag('SITE_CENTRIC_TOGGLE').isEnabled;
    const [isTrialAlertCollapsed, setIsTrialAlertCollapsed] = useRecoilState(
        organizationTrialAlertCollapsedAtom(organization.id)
    );
    const hasCollapsibleTrialAlert =
        organization.trial.status === api.BillingTrialStatus.Active &&
        Boolean(organization.trial.endDate);

    return (
        <>
            <SidebarFooter className="mb-2 pb-8 [&:has(>#customerio-alert:only-child:empty)]:hidden">
                {hasCollapsibleTrialAlert ? (
                    <CollapsibleOrganizationAlert
                        key={organization.id}
                        organization={organization}
                        collapsed={isTrialAlertCollapsed}
                        onCollapse={() => {
                            setIsTrialAlertCollapsed(true);
                        }}
                    />
                ) : (
                    <OrganizationAlert data-org-alerts organization={organization} />
                )}
                <CustomerIOAlert
                    onBannerAppears={
                        canToggleSiteCentricNav
                            ? preloadSiteCentricNavConfirmDialogImage
                            : undefined
                    }
                />
                {canToggleSiteCentricNav ? (
                    <SiteCentricNavConfirmDialog
                        organizationId={organization.id}
                        isSiteCentricNavEnabled={isSiteCentricNavEnabled}
                    />
                ) : null}
            </SidebarFooter>

            <div className="absolute inset-x-2 bottom-2 z-[2] flex items-center gap-1 bg-gradient-to-t from-30% from-[--sp-color-bg-side] to-transparent">
                <DocsButton organization={organization} screen={screen} />
                <HelpButton />
                <FeedbackSidebarButton label="Share feedback" outlined iconOnly />
                <CollapsedOrganizationTrialAlertButton
                    visible={hasCollapsibleTrialAlert && isTrialAlertCollapsed}
                    onPress={() => {
                        setIsTrialAlertCollapsed(false);
                    }}
                />
                {!isSiteCentricNavEnabled ? (
                    <SidebarButton
                        iconOnly
                        outlined
                        label="Settings"
                        icon={Icon.Settings}
                        onPress={() => {
                            navigate({
                                screen: {
                                    type: 'org_settings',
                                    organization: organization.id,
                                    tab: { type: 'profile' },
                                },
                            });
                        }}
                    />
                ) : null}
                {isSiteCentricNavEnabled ? (
                    <AgentsLink screen={screen} iconOnly outlined rounded />
                ) : null}
            </div>
        </>
    );
});

function DocsButton(props: { organization: api.Organization; screen: NavigationScreenState }) {
    const { organization, screen } = props;

    return (
        <PaletteButton
            label="Docs"
            leadingIcon={Icon.Documentation}
            iconOnly
            kind="secondary"
            rounded
            palette={() =>
                createPalette({
                    groups: [
                        {
                            key: 'docs',
                            commands: [
                                {
                                    key: 'docs.documentation',
                                    title: 'Documentation',
                                    icon: Icon.BookOpen,
                                    trigger: () => {
                                        analytics.logEvent({
                                            type: 'click',
                                            action: 'open',
                                            organization: organization.id,
                                            screen: screen.type,
                                            screenSection: 'sidebar_footer',
                                            component: 'link_documentation',
                                        });
                                        window.open(createDocumentationURL(''), '_blank');
                                    },
                                },
                                {
                                    key: 'docs.guides',
                                    title: 'Guides',
                                    icon: Icon.Documentation,
                                    trigger: () => {
                                        analytics.logEvent({
                                            type: 'click',
                                            action: 'open',
                                            organization: organization.id,
                                            screen: screen.type,
                                            screenSection: 'sidebar_footer',
                                            component: 'link_guides',
                                        });
                                        window.open(createDocumentationURL('guides'), '_blank');
                                    },
                                },
                            ],
                        },
                    ],
                })
            }
        />
    );
}

/**
 * Home sidebar links.
 */
export function HomeLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;

    return (
        <SidebarLink
            label="Home"
            icon={Icon.Home}
            isActive={screen.type === 'home'}
            href={{
                screen: {
                    type: 'home',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

export function SitesLink(props: { screen: NavigationScreenState; label?: string }) {
    const { screen, label = 'Docs sites' } = props;

    return (
        <SidebarLink
            label={label}
            icon={Icon.DocsSite}
            isActive={screen.type === 'sites'}
            href={{
                screen: {
                    type: 'sites',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

function ContentScreenLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <Tooltip label="Find all your spaces here" position="right">
            <SidebarLink
                label="Content"
                icon={Icon.AllSpaces}
                isActive={screen.type === 'org_content'}
                href={{
                    screen: {
                        type: 'org_content',
                        organization: 'organization' in screen ? screen.organization : undefined,
                    },
                }}
            />
        </Tooltip>
    );
}

function OrganizationTrashLink(props: { screen: NavigationScreenState; organizationId: string }) {
    const { screen, organizationId } = props;
    return (
        <SidebarLink
            label="Trash"
            icon={Icon.Trashcan}
            isActive={screen.type === 'trash'}
            href={{
                screen: {
                    type: 'trash',
                    organization: organizationId,
                },
            }}
        />
    );
}

function OpenAPISettingsLink(props: { organizationId: string }) {
    return (
        <SidebarLink
            label="OpenAPI"
            icon={Icon.OpenAPI}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: props.organizationId,
                    tab: { type: 'openapi' },
                },
            }}
        />
    );
}

function TranslationsSettingsLink(props: { organizationId: string }) {
    const trialingPlan = useTrialingOrganizationFeaturePlan('translations');

    return (
        <SidebarLink
            label="Translations"
            icon={Icon.AITranslate}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: props.organizationId,
                    tab: { type: 'translations', view: 'overview' },
                },
            }}
        >
            <SidebarTrialingBadge plan={trialingPlan} />
        </SidebarLink>
    );
}

function IntegrationsSettingsLink(props: { organizationId: string }) {
    return (
        <SidebarLink
            label="Integrations"
            icon={Icon.Integrations}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: props.organizationId,
                    tab: { type: 'integrations' },
                },
            }}
        />
    );
}

export function McpSettingsLink() {
    return (
        <SidebarLink
            label="MCP"
            icon={Icon.MCP}
            href={{
                screen: {
                    type: 'account',
                    tab: 'developer',
                    developerTab: 'mcp',
                },
            }}
        />
    );
}

/**
 * Sidebar list of the organization's styleguides, shown in place of the spaces
 * tree when a styleguide is opened at the org level.
 */
function StyleguidesContentList(props: { organizationId: string; screen: NavigationScreenState }) {
    const { organizationId, screen } = props;
    const { data } = useSuspenseQuery(apiQuery('listOrganizationStyleguides', { organizationId }));
    const currentSpace = screen.type === 'space' ? screen.space : undefined;

    return (
        <>
            {data.styleguides.map((styleguide) => (
                <SidebarLink
                    key={styleguide.space.id}
                    label={styleguide.space.title}
                    icon={Icon.BookOpen}
                    isActive={currentSpace === styleguide.space.id}
                    href={{
                        screen: {
                            type: 'space',
                            organization: organizationId,
                            space: styleguide.space.id,
                            page: '',
                        },
                    }}
                />
            ))}
        </>
    );
}

/**
 * Site-level styleguide link shown below Git Sync in the Tools section. Points to
 * the styleguide space when the site already has one, otherwise to the styleguide
 * creation page where a styleguide can be created or picked (admins only).
 */
function SiteStyleguideLink(props: {
    site: api.Site;
    organizationId: string;
    screen: NavigationScreenState;
    canAdminSite: boolean;
}) {
    const { site, organizationId, screen, canAdminSite } = props;
    const isStyleguideEnabled = useFlag('STYLEGUIDE').isEnabled;
    const { navigate } = useNavigation();

    if (!isStyleguideEnabled) {
        return null;
    }

    const styleguideSpace = site.styleguide?.space;

    // Without a styleguide yet, only admins can create one — hide the link otherwise.
    if (!styleguideSpace && !canAdminSite) {
        return null;
    }

    const link = (
        <SidebarLink
            label="Styleguide"
            icon={Icon.BookOpen}
            isActive={
                styleguideSpace
                    ? screen.type === 'space' && screen.space === styleguideSpace
                    : screen.type === 'styleguide_new' && screen.site === site.id
            }
            href={
                styleguideSpace
                    ? {
                          screen: {
                              // Keep the URL scoped to the site the styleguide belongs to.
                              type: 'space',
                              space: styleguideSpace,
                              organization: organizationId,
                              site: site.id,
                              page: '',
                          },
                      }
                    : {
                          screen: {
                              type: 'styleguide_new',
                              organization: organizationId,
                              site: site.id,
                          },
                      }
            }
        >
            {!styleguideSpace ? <span className="ml-auto text-muted">Set up</span> : null}
        </SidebarLink>
    );

    if (!styleguideSpace) {
        return link;
    }

    return (
        <div className="group/sidebar-item relative">
            {link}
            {canAdminSite ? (
                <div className="absolute inset-y-0 right-1 flex items-center">
                    <SidebarActionsButton
                        palette={() =>
                            createPalette({
                                groups: [
                                    {
                                        key: 'styleguide',
                                        commands: [
                                            {
                                                key: 'detach',
                                                title: 'Detach styleguide',
                                                icon: Icon.LinkBroken,
                                                trigger: () =>
                                                    confirmDetachStyleguide({
                                                        organizationId,
                                                        siteId: site.id,
                                                        siteTitle: site.title,
                                                        navigate,
                                                    }),
                                            },
                                        ],
                                    },
                                ],
                            })
                        }
                    />
                </div>
            ) : null}
        </div>
    );
}

export function AgentsLink(props: SidebarLinkProps & { screen: NavigationScreenState }) {
    const { screen, iconOnly, rounded, outlined } = props;
    const featureAgentEnabled = useFlag('DOCS_AGENTS').isEnabled;

    if (featureAgentEnabled) {
        return null;
    }

    return (
        <SidebarLink
            label="GitBook Agent"
            icon={Icon.AIAgent}
            iconOnly={iconOnly}
            rounded={rounded}
            outlined={outlined}
            isActive={screen.type === 'agents'}
            href={{
                screen: {
                    type: 'agents',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

/**
 * Site sidebar links.
 */
function OverviewLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Overview"
            icon={Icon.Grid}
            isActive={screen.type === 'site' && !screen.tab && !screen.sideSheet}
            href={{
                screen: {
                    type: 'site',
                    site: 'site' in screen ? screen.site : undefined,
                    tab: undefined,
                    sideSheet: undefined,
                },
            }}
        />
    );
}

function StructureLink(props: { screen: NavigationScreenState; trialingPlan: string | null }) {
    const { screen, trialingPlan } = props;
    return (
        <SidebarLink
            label="Site structure"
            icon={Icon.SiteStructure}
            isActive={screen.type === 'site' && screen.tab?.type === 'structure'}
            href={{
                screen: {
                    type: 'site',
                    site: 'site' in screen ? screen.site : undefined,
                    tab: { type: 'structure' },
                },
            }}
        >
            <SidebarTrialingBadge plan={trialingPlan} />
        </SidebarLink>
    );
}

function SiteMembersLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Members"
            icon={Icon.Users}
            isActive={screen.type === 'site' && screen.tab?.type === 'members'}
            href={{
                screen: {
                    type: 'site',
                    site: 'site' in screen ? screen.site : undefined,
                    tab: { type: 'members' },
                },
            }}
        />
    );
}

export function ChangeRequestsLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Change requests"
            icon={Icon.ChangeRequest}
            isActive={screen.type === 'change_requests'}
            href={{
                screen: {
                    type: 'change_requests',
                    organization: 'organization' in screen ? screen.organization : undefined,
                    site: 'site' in screen ? screen.site : undefined,
                },
            }}
        />
    );
}

function GitSyncSidebarLink(props: {
    site: api.Site;
    organizationId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, screen, site } = props;
    const hasInstallation = hasGitSyncInstallation(site);
    const installationProvider = site.gitSync?.installationProvider;
    // The sidebar renders beside the screen rather than under it, so it is outside the Git Sync
    // target provider. A site has no operation channel of its own, so its live status is exactly
    // this fallback — the only thing missed is a sync just triggered from the screen.
    const gitSyncStatus = getFallbackGitSyncCurrentStatus(site);
    const isPending =
        site.gitSync?.installationStatus &&
        site.gitSync.installationStatus !== api.GitSyncInstallationStatus.Active;
    const presentation = getGitSyncStatusPresentation({
        gitSyncStatus,
        hasInstallation,
        isPending: Boolean(isPending),
    });

    // A failed sync opens the sync status view (matching the space behaviour) instead of the
    // configuration. This link only renders behind the native site flow, so no extra flag gate is
    // needed here.
    const isSyncFailed = isFailedGitSyncStatus(gitSyncStatus);
    const setupTarget = getSiteGitSyncSetupTarget(
        site,
        organizationId,
        isSyncFailed && site.gitSync?.installationId ? 'status' : 'setup'
    );

    const sidebarStatus = gitSyncStatus ? (
        <Status
            kind={presentation.kind}
            label={presentation.label}
            size="small"
            loading={presentation.loading}
            detail={presentation.detail}
            detailTooltip
        />
    ) : presentation.actionLabel ? (
        <span className="text-muted">{presentation.actionLabel}</span>
    ) : null;

    return (
        <SidebarLink
            label="Git Sync"
            icon={installationProvider === api.GitSyncProvider.Gitlab ? Icon.GitLab : Icon.GitHub}
            isActive={screen.type === 'site' && screen.sideSheet?.type === 'gitsync'}
            tooltip={{
                position: 'right',
                label: gitSyncStatus ? undefined : presentation.detail,
            }}
            href={setupTarget}
        >
            {sidebarStatus ? <span className="ml-auto">{sidebarStatus}</span> : null}
        </SidebarLink>
    );
}

function MembersLink(props: { screen: NavigationScreenState; organizationId: string }) {
    const { organizationId } = props;
    return (
        <SidebarLink
            label="Members"
            icon={Icon.Users}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: organizationId,
                    tab: { type: 'members' },
                },
            }}
        />
    );
}

function BillingLink(props: { screen: NavigationScreenState; organizationId: string }) {
    const { organizationId } = props;
    return (
        <SidebarLink
            label="Billing"
            icon={Icon.Upgrade}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: organizationId,
                    tab: { type: 'plans' },
                },
            }}
        />
    );
}

function OrganizationSettingsLink(props: { organizationId: string }) {
    return (
        <SidebarLink
            label="Settings"
            icon={Icon.Settings}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: props.organizationId,
                    tab: { type: 'profile' },
                },
            }}
        />
    );
}

function SiteTriageLink(props: {
    organizationId: string;
    siteId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, siteId, screen } = props;

    return (
        <SidebarLink
            label="Triage"
            icon={Icon.Findings}
            isActive={screen.type === 'triage' && screen.site === siteId}
            href={{
                screen: {
                    type: 'triage',
                    organization: organizationId,
                    site: siteId,
                    // Reset to the bare `/triage` findings view, clearing any selected
                    // finding, sub-view, scan, or filters carried over from the current screen.
                    finding: undefined,
                    view: undefined,
                    filters: undefined,
                    scan: undefined,
                    scanFilters: undefined,
                } satisfies NavigationScreenTriageState,
            }}
        >
            <span className="ml-auto">
                <NewFeatureBadge flagKey="SITE_CONTEXT_CHANGE_REQUEST" />
            </span>
        </SidebarLink>
    );
}

function SiteContentGapsLink(props: {
    organizationId: string;
    siteId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, siteId, screen } = props;

    return (
        <SidebarLink
            label="Content gaps"
            icon={Icon.FindingContentGap}
            isActive={screen.type === 'content_gaps' && screen.site === siteId}
            href={{
                screen: {
                    type: 'content_gaps',
                    organization: organizationId,
                    site: siteId,
                    // Reset to the bare `/content-gaps` view, clearing any selected finding,
                    // sub-view or filters carried over from the current screen.
                    finding: undefined,
                    view: undefined,
                    filters: undefined,
                } satisfies NavigationScreenContentGapsState,
            }}
        />
    );
}

function ContentSidebarSkeleton() {
    return (
        <div className="flex flex-col gap-3">
            {/* <Divider /> */}
            <div className="space-y-2 px-3 pr-1.5">
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
            </div>
            {/* <Divider /> */}
            <div className="space-y-2 px-3 pr-1.5">
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
            </div>
        </div>
    );
}
